import * as Paho from 'paho-mqtt';

import { featuresDoneSetup } from 'common/store/workspaces/actions';

import { fetchSignedWebSocketUrl } from 'common/store/session/actions';
import { GradingUpdateEvent } from 'common/store/common/constants';
import { isNullOrUndefined } from 'common/util/utils';
import store from '../store';
import { RootState } from '../store/rootReducer';
import {
    gradingPartyIndexUpdate,
    headerUpdated,
    updateGradingInfo,
    updateGradingInfoWithGradingParty,
    userQuestionIndexUpdate,
    assessmentRunQuestionsUpdate,
    userQuestionIndexCorrectedAnswerKeysUpdate,
} from '../store/assessment/actions';
import {
    pushScoreUpdateNotification,
    annotationsUpdate,
    pushAuditTrailUpdateNotification,
} from '../store/common/actions';
import { showReload } from '../components/reload/actions';
import {
    getGradingWorkflowInfo, loadAssessmentRunSession,
    setupWorkspaces, submissionDeleted,
    updateQuestionsForPartyWithNewRevisionIds,
} from '../store/context/actions';
import { loadExplanation, setExplanationLocked } from '../features/explanationOfGrades/actions';
import { reloadDraft } from '../features/workflowSettings/actions';
import { studentReportUpdate } from '../features/studentReport/actions';
import { quickMarkChanged } from '../features/setMark/actions';
import { onPlagiarismStatusUpdate } from '../features/submission/actions';
import { loadAssessmentRunFeedback, setAssessmentRunFeedbackLocked } from '../features/assessmentRunFeedback/actions';

const uuid = require('uuid');

interface TopicCallbackMap {
    [key: string]: any;
}

const topicCallbackMap: TopicCallbackMap = {};
const RECONNECT_INTERVAL = 20 * 1000;
let awtClient: Paho.Client;

const showReloadWindow = () => {
    store.dispatch(showReload({
        titleKey: 'grading.reload.workspaceChanged.title',
        messageKey: 'grading.reload.workspaceChanged.message',
    }));
};

const awsIOTService = {
    reconnectClient(): void {
        setTimeout(
            () => {
                window.console.info('Reconnecting to AWS IoT');
                store.dispatch(fetchSignedWebSocketUrl());
            },
            RECONNECT_INTERVAL
        );
    },
    onConnectionLost(e: Paho.MQTTError): void {
        window.console.info('Client disconnected from AWS IoT', e);
        this.reconnectClient();
    },
    onMessageArrived(message: Paho.Message): void {
        const messageObject = JSON.parse(message.payloadString);
        const callback = topicCallbackMap[message.destinationName];
        if (typeof callback === 'function') {
            callback(messageObject.data);
        }
        window.console.info('Message from AWS IoT', message.payloadString);
    },
    subscribeToTopic(topic: string, callback: any): void {
        window.console.log(`Client subscribing to ${topic}`);

        if (awtClient) {
            awtClient.subscribe(topic);
        }

        topicCallbackMap[topic] = callback;
    },
    buildTopic(topic: string, postfix?: any): string {
        let completeTopic = topic;
        const devEnv = (window as any).deployEnvironment;
        if (postfix) {
            completeTopic = `${completeTopic}_${postfix}`;
        }

        return `${devEnv}_${completeTopic}`;
    },
    onIOTConnectSuccess(client: Paho.Client): void {
        awtClient = client;
        window.console.log('Client connected to AWS IoT, listen to topics');
        const { assessmentRunId, assessmentRunSession } = (store.getState() as RootState).context;

        this.subscribeToTopic(this.buildTopic('grading-update', assessmentRunId), (message: any) => {
            const userId = (store.getState() as RootState).session.user.id;

            const messageScoreUpdate = message[GradingUpdateEvent.scoreUpdate];
            const messageWorkspaceUpdate = message[GradingUpdateEvent.workspaceUpdate];
            const messageShowReloadUpdate = message[GradingUpdateEvent.showReloadUpdate];
            const messageExplanationUpdate = message[GradingUpdateEvent.explanationUpdate];
            const messageExplanationLockUpdate = message[GradingUpdateEvent.explanationLockUpdate];
            const messageAssessmentInfoUpdate = message[GradingUpdateEvent.assessmentInfoUpdate];
            const messageGradingPartyIndexUpdate = message[GradingUpdateEvent.gradingPartyIndexUpdate];
            const messageHeaderUpdate = message[GradingUpdateEvent.headerUpdate];
            const messageUserQuestionIndexUpdateForCandidate = message[GradingUpdateEvent.userQuestionIndexUpdateForCandidate];
            const messageAnnotationsUpdate = message[GradingUpdateEvent.annotationsUpdate];
            const messageAssessmentRunDraftUpdate = message[GradingUpdateEvent.assessmentRunDraftUpdate];
            const messageAssessmentRunQuestionIndexUpdate = message[GradingUpdateEvent.assessmentRunQuestionIndexUpdate];
            const messageStudentReportUpdate = message[GradingUpdateEvent.studentReportUpdate];
            const messageAuditTrailUpdate = message[GradingUpdateEvent.auditTrailUpdate];
            const messageQuickMarkChanged = message[GradingUpdateEvent.quickMarkChanged];
            const messagePlagiarismUpdate = message[GradingUpdateEvent.plagiarismUpdate];
            const correctedQuestionsUpdate = message[GradingUpdateEvent.correctedQuestionsUpdate];
            const assessmentRunSessionUpdate = message[GradingUpdateEvent.assessmentRunSessionUpdate];
            const submissionDeletedUpdate = message[GradingUpdateEvent.submissionDeletedUpdate];
            const assessmentRunFeedbackLockUpdate = message[GradingUpdateEvent.assessmentRunFeedbackLockUpdate];
            const assessmentRunFeedbackUpdate = message[GradingUpdateEvent.assessmentRunFeedbackUpdate];

            if (messageScoreUpdate && !messageAssessmentInfoUpdate) {
                store.dispatch(pushScoreUpdateNotification(messageScoreUpdate, message.questionId));
            }
            if (messageAuditTrailUpdate) {
                store.dispatch(pushAuditTrailUpdateNotification());
            }
            if (messageWorkspaceUpdate) {
                if (message.triggeringUser === userId || message.silentReload) {
                    // reload workspaces without the reload panel
                    store.dispatch(setupWorkspaces());
                } else {
                    showReloadWindow();
                }
            }
            if (messageShowReloadUpdate) {
                showReloadWindow();
            }
            if (messageExplanationUpdate
                    && messageExplanationUpdate.length > 0) {
                messageExplanationUpdate
                    .forEach((explanationId: number) => store.dispatch(loadExplanation(undefined, explanationId)));
            }

            if (messageExplanationLockUpdate) {
                store.dispatch(setExplanationLocked(undefined, message));
                if (!messageExplanationLockUpdate.locked && message.triggeringUser !== userId) {
                    store.dispatch(loadExplanation(undefined, messageExplanationLockUpdate.explanationId));
                }
            }

            if (messageAssessmentInfoUpdate && messageGradingPartyIndexUpdate) {
                // need to fetch them in sync
                store.dispatch(updateGradingInfoWithGradingParty(messageGradingPartyIndexUpdate));
            }

            if (messageAssessmentInfoUpdate && !messageGradingPartyIndexUpdate) {
                store.dispatch(updateGradingInfo(message.triggeringUser));
            }

            if (messageHeaderUpdate) {
                store.dispatch(getGradingWorkflowInfo());
                store.dispatch(headerUpdated());
            }

            if (messageGradingPartyIndexUpdate && !messageAssessmentInfoUpdate) {
                store.dispatch(gradingPartyIndexUpdate(messageGradingPartyIndexUpdate));
            }

            if (messageUserQuestionIndexUpdateForCandidate) {
                store.dispatch(userQuestionIndexUpdate(messageUserQuestionIndexUpdateForCandidate));
                if (correctedQuestionsUpdate) {
                    store.dispatch(userQuestionIndexCorrectedAnswerKeysUpdate());
                }
            }

            if (messageAnnotationsUpdate) {
                store.dispatch(annotationsUpdate());
            }

            if (messageAssessmentRunDraftUpdate) {
                store.dispatch(reloadDraft('feature_workflowSettings_overlay',
                    messageAssessmentRunDraftUpdate, message.draftType, message.draftDeleted));
            }

            if (messageAssessmentRunQuestionIndexUpdate) {
                store.dispatch(assessmentRunQuestionsUpdate());
            }

            if (messageStudentReportUpdate) {
                store.dispatch(studentReportUpdate('student_report'));
            }

            if (messageQuickMarkChanged) {
                store.dispatch(quickMarkChanged());
            }

            if (messagePlagiarismUpdate) {
                store.dispatch(onPlagiarismStatusUpdate());
            }

            if (correctedQuestionsUpdate) {
                store.dispatch(updateQuestionsForPartyWithNewRevisionIds(correctedQuestionsUpdate));
            }

            if (assessmentRunSessionUpdate) {
                if (isNullOrUndefined(assessmentRunSession)
                        || assessmentRunSession.assessmentRunSessionId === assessmentRunSessionUpdate) {
                    store.dispatch(loadAssessmentRunSession(assessmentRunSessionUpdate));
                }
            }

            if (submissionDeletedUpdate) {
                store.dispatch(submissionDeleted(submissionDeletedUpdate));
            }

            if (assessmentRunFeedbackUpdate) {
                store.dispatch(loadAssessmentRunFeedback());
            }

            if (assessmentRunFeedbackLockUpdate) {
                store.dispatch(setAssessmentRunFeedbackLocked(message));
            }
        });
        this.subscribeToTopic(this.buildTopic('annotation-updates', assessmentRunId), () => {
            store.dispatch(featuresDoneSetup());
        });
    },
    onIOTConnectFailure(e: Paho.MQTTError): void {
        window.console.log('Client failed to connect to AWS IoT', e);
        this.reconnectClient();
    },
    setupClient(signedWebSocketUrl: string): void {
        if (window.Paho === undefined) {
            window.Paho = { MQTT: Paho };
        }

        if (!signedWebSocketUrl) {
            window.console.warn('No web socket url available!');
            return;
        }
        const client: Paho.Client = new Paho.Client(signedWebSocketUrl, uuid.v4());

        const connectOptions: Paho.ConnectionOptions = {
            useSSL: true,
            timeout: 3,
            mqttVersion: 4,
            onSuccess: () => this.onIOTConnectSuccess(client),
            onFailure: (e: Paho.MQTTError) => this.onIOTConnectFailure(e),
        };

        client.onMessageArrived = (message: Paho.Message) => {
            this.onMessageArrived(message);
        };

        client.onConnectionLost = (e: Paho.MQTTError) => {
            this.onConnectionLost(e);
        };

        client.connect(connectOptions);
    },
};

export default awsIOTService;
